import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { Image, Loader } from '@storybook';
import { LOGO, BRAND_LOGO } from 'constant';
import {
	IframePropsState,
	IsBannerStatusState,
	IsKycLoading,
	IsOpenInIframeState,
	SelectedMultipleDateState,
	kycSettingsState,
} from 'global-stores';

import { loginState } from 'global-stores';
import { NodesState } from 'views/complex-onboarding-flow/stores/states';
import { ThemeToggle } from 'views/design-system';
import {
	ConfigureSurvayFormState,
	ConfiguredStepState,
} from 'views/onboarding-flow/store';
import { SendNotificationState } from 'views/pipeline/store';
import { OnboardingFlowBackModel } from 'components';
import { OnboardingProgress } from '../onboarding-progress';
import './header.scss';

export const OnboardingHeader = () => {
	const navigateToOnboarding = useNavigate();
	const isWebComponentOpen = useRecoilValue(IsOpenInIframeState);
	const nodes = useRecoilValue(NodesState);
	const { webAdminType, clientlogo = '' } = useRecoilValue(IframePropsState);
	const { whitelabel, logo } = useRecoilValue(kycSettingsState);
	const isLogoLoaded = useRecoilValue(IsKycLoading);
	const { firstName, lastName, name, email, sandboxStatus, image } =
		useRecoilValue(loginState) as any;
	const bannerStatus = useRecoilValue(IsBannerStatusState);
	const resetFormStepState = useResetRecoilState(ConfigureSurvayFormState);
	const resetReminderDayList = useResetRecoilState(SendNotificationState);
	const setSelectedMultipleDate = useSetRecoilState(SelectedMultipleDateState);
	const resetConfiguredStep = useResetRecoilState(ConfiguredStepState);
	const [isBackModel, setIsBackModal] = useState(false);
	const [isBackLoading, setIsBackLoading] = useState(false);

	const handleLogoClick = useCallback(() => {
		setIsBackModal(true);
	}, []);

	const handleLeaveClick = useCallback(() => {
		setIsBackLoading(true);
		setTimeout(() => {
			setIsBackModal(false);
			navigateToOnboarding('/onboarding');
			resetReminderDayList();
			setSelectedMultipleDate([]);
			resetFormStepState();
			resetConfiguredStep();
		}, 1000);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleInput = useCallback(() => {
		setIsBackModal(false);
	}, []);

	const webComponentImage = useMemo(() => {
		return (
			<img
				src={clientlogo || logo || BRAND_LOGO}
				alt="logo"
				className="sidebar-container__companyLogo"
			/>
		);
	}, [clientlogo, logo]);

	const logoUrl = useMemo(() => {
		if (clientlogo?.length) {
			return clientlogo;
		} else return LOGO.LIGHT;
	}, [clientlogo]);

	const renderBrandLogo = useMemo(() => {
		return webAdminType === 'onboarded' || isWebComponentOpen || whitelabel ? (
			webComponentImage
		) : (
			<img
				src={logoUrl}
				className="sidebar-container__brandLogo__img"
				alt="logo"
			/>
		);
	}, [
		isWebComponentOpen,
		logoUrl,
		webAdminType,
		webComponentImage,
		whitelabel,
	]);

	return (
		<div
			className="OnboardingHeader--container"
			style={{ top: bannerStatus === false ? '44px' : '0px' }}
		>
			<div className="OnboardingHeader--left">
				<div className="OnboardingHeader--logo" onClick={handleLogoClick}>
					{isLogoLoaded ? <Loader dimension={24} /> : renderBrandLogo}
				</div>
				<div className="OnboardingHeader--steps">
					{/* The flow's own name used to sit here, beside the logo. It is
						asked for on Publish and carries no meaning while the canvas is
						being built -- an autogenerated "Onboarding flow 1" next to the
						brand mark read as a second, competing title. */}
					<div className="OnboardingHeader--subtitle">
						{nodes?.length ?? 0} node{nodes?.length === 1 ? '' : 's'} on canvas
					</div>
				</div>
			</div>

			{/* WHAT THE BUILDER WAS MISSING. This is the one screen where the
			    account genuinely disappears — `routes-children.tsx` removes the
			    whole rail on /onboarding-flow so the wizard can have the width —
			    and the thing that actually has teeth is knowing WHICH ACCOUNT and
			    WHICH MODE you are about to publish a flow under.

			    Context, not a control: no menu, no log out. Account actions here
			    would invite abandoning a half-built canvas, and the way out of the
			    builder is Back or Publish. ThemeToggle moves here too — it is a
			    utility, and on every other sv2 surface utilities sit in the
			    trailing corner rather than between the title and empty space. */}
			<div className="OnboardingHeader--right">
				<ThemeToggle />
				{webAdminType !== 'onboarded' && !isWebComponentOpen && (
					<div
						className={`OnboardingHeader--account${
							sandboxStatus ? ' OnboardingHeader--account--sandbox' : ''
						}`}
						title={`${email}${sandboxStatus ? ' — SANDBOX' : ''}`}
					>
						{image ? (
							<Image url={image} className="OnboardingHeader--account-avatar" />
						) : (
							<span className="OnboardingHeader--account-avatar OnboardingHeader--account-avatar--mono">
								{((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase() ||
									'ME'}
							</span>
						)}
						<span className="OnboardingHeader--account-name">
							{[firstName, lastName].filter(Boolean).join(' ') || name || email}
						</span>
						{sandboxStatus && (
							<i className="OnboardingHeader--account-mode">Sandbox</i>
						)}
					</div>
				)}
			</div>
			{/* The tab strip is a row of its own beneath the identity row, so it
				starts at the header's own left padding instead of being indented
				past the brand square -- `.tabs` sits next to `.hrow` in the
				concept, not inside it. */}
			<OnboardingProgress />
			<OnboardingFlowBackModel
				isOpen={isBackModel}
				isLoaded={isBackLoading}
				handleProceedBack={handleLeaveClick}
				handleInput={handleInput}
			/>
		</div>
	);
};
